import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import EnvironmentManager from 'Utils/environmentManager';
import { buildBroadageWidgetUrl, resolveBroadageRedirectParams } from 'Utils/buildBroadageWidgetUrl';
import {
  getUserTheme,
  normalizeTheme,
  THEME_CHANGE_EVENT,
  USER_THEME_ATTR,
  USER_THEME_KEY,
} from 'Utils/theme';
import { postThemeToWidgetIframe } from 'Utils/widgetThemeSync';

const BILYONER_LOADER_SRC = '/public/loaders/broadage-loader.webp';
const LOADER_SAFETY_TIMEOUT = 20000;
const DEFAULT_MIN_HEIGHT = 500;
const WIDGET_URL_KEY = 'widget_broadage_url';

const extractHeightFromMessage = data => {
  if (data && typeof data === 'object') {
    return data.height || data.value || data.iframe_height || data.iframeHeight
      || (data.size && data.size.height) || (data.data && data.data.height);
  }
  if (typeof data === 'number' && data > 50) return data;
  if (typeof data === 'string') {
    const parsed = parseInt(data, 10);
    if (parsed > 50) return parsed;
  }
  return null;
};

const readWidgetTemplate = () => EnvironmentManager.getMessageText({ key: WIDGET_URL_KEY });

const getPageLocation = () => {
  if (typeof window === 'undefined' || !window.location) {
    return { origin: '', search: '' };
  }
  return {
    origin: window.location.origin,
    search: window.location.search,
  };
};

const readRedirectParams = () => {
  const { origin, search } = getPageLocation();
  return resolveBroadageRedirectParams({
    getMessageText: matchObject => EnvironmentManager.getMessageText(matchObject),
    origin,
    search,
  });
};

function BroadageWidget (props) {
  const {
    broadageEventId,
    broadageLeagueId,
    broadageTeamId,
    type,
    title,
    sportType,
    homeTeamId,
    awayTeamId,
    teamInfo,
    roundNavigationRule,
    dataListHeight,
    theme,
  } = props;

  const iframeRef = useRef(null);
  const initialThemeRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [iframeHeight, setIframeHeight] = useState(null);
  const [template, setTemplate] = useState(() => readWidgetTemplate());
  const [redirectParams, setRedirectParams] = useState(() => readRedirectParams());
  const [activeTheme, setActiveTheme] = useState(() => (
    theme ? normalizeTheme(theme) : getUserTheme()
  ));

  useEffect(() => {
    let cancelled = false;

    const applyCmsValues = () => {
      setTemplate(readWidgetTemplate());
      setRedirectParams(readRedirectParams());
    };

    const resolveTemplate = async () => {
      if (!cancelled) applyCmsValues();
      await EnvironmentManager.waitForMessages();
      if (cancelled) return;
      applyCmsValues();
    };

    resolveTemplate();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (theme) {
      setActiveTheme(normalizeTheme(theme));
      return undefined;
    }

    const syncTheme = () => setActiveTheme(getUserTheme());

    const handleThemeChange = () => syncTheme();
    const handleStorage = event => {
      if (event.key === USER_THEME_KEY || event.key === null) {
        syncTheme();
      }
    };

    syncTheme();
    window.addEventListener(THEME_CHANGE_EVENT, handleThemeChange);
    window.addEventListener('storage', handleStorage);

    let observer;
    if (typeof MutationObserver !== 'undefined' && document?.documentElement) {
      observer = new MutationObserver(mutations => {
        if (mutations.some(m => m.attributeName === USER_THEME_ATTR)) {
          syncTheme();
        }
      });
      observer.observe(document.documentElement, {
        attributes: true,
        attributeFilter: [USER_THEME_ATTR],
      });
    }

    return () => {
      window.removeEventListener(THEME_CHANGE_EVENT, handleThemeChange);
      window.removeEventListener('storage', handleStorage);
      observer?.disconnect();
    };
  }, [theme]);

  if (initialThemeRef.current == null) {
    initialThemeRef.current = activeTheme;
  }

  const iframeSrc = useMemo(() => buildBroadageWidgetUrl({
    template,
    params: {
      broadageEventId,
      sportType,
      type,
      broadageTeamId,
      broadageLeagueId,
      teamInfo,
      roundNavigationRule,
      homeTeamId,
      awayTeamId,
      dataListHeight,
      theme: initialThemeRef.current,
      ...redirectParams,
    },
  }), [
    template,
    broadageEventId,
    sportType,
    type,
    broadageTeamId,
    broadageLeagueId,
    teamInfo,
    roundNavigationRule,
    homeTeamId,
    awayTeamId,
    dataListHeight,
    redirectParams,
  ]);

  useEffect(() => {
    postThemeToWidgetIframe(iframeRef.current, activeTheme);
  }, [activeTheme]);

  useEffect(() => {
    if (!iframeSrc) return;
    setIsLoading(true);
    setIframeHeight(null);
  }, [iframeSrc]);

  useEffect(() => {
    const handleMessage = event => {
      const iframe = iframeRef.current;
      if (!iframe) return;

      // Ignore messages from other frames when we can identify the source.
      if (event.source && event.source !== iframe.contentWindow) return;

      const height = extractHeightFromMessage(event.data);
      if (height && height > 50) {
        setIframeHeight(height);
        setIsLoading(false);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  useEffect(() => {
    if (!isLoading || !iframeSrc) return undefined;
    const safetyTimeout = setTimeout(() => setIsLoading(false), LOADER_SAFETY_TIMEOUT);
    return () => clearTimeout(safetyTimeout);
  }, [isLoading, iframeSrc]);

  const handleIframeLoad = useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe || !iframeSrc) return;

    const loadedSrc = iframe.getAttribute('src') || iframe.src || '';
    if (!loadedSrc || loadedSrc === 'about:blank') return;

    try {
      const doc = iframe.contentDocument || iframe.contentWindow?.document;
      if (doc) {
        const height = Math.max(
          doc.body?.scrollHeight || 0,
          doc.body?.offsetHeight || 0,
          doc.documentElement?.scrollHeight || 0,
          doc.documentElement?.offsetHeight || 0
        );
        if (height > 50) setIframeHeight(height);
      }
    } catch (err) {
      // cross-origin — height will come via postMessage
    }

    // Document is ready: drop the Bilyoner loader here. Waiting for postMessage
    // height kept the spinner up long after the widget had already loaded.
    // Iframe stays color-scheme matched and was hidden until this point, so the
    // blank white document never paints.
    setIsLoading(false);
    postThemeToWidgetIframe(iframe, activeTheme);
  }, [iframeSrc, activeTheme]);

  if (!iframeSrc) {
    return (
      <div className="broadage-widget">
        <div className="broadage-widget__content broadage-widget__content--loading">
          <div className="broadage-widget__loader">
            <img
              className="broadage-widget__loader-image"
              src={ BILYONER_LOADER_SRC }
              alt=""
            />
          </div>
        </div>
      </div>
    );
  }

  const iframeStyle = {
    border: 'none',
    display: 'block',
    width: '100%',
    ...(iframeHeight
      ? { height: `${iframeHeight}px` }
      : { minHeight: `${DEFAULT_MIN_HEIGHT}px` }),
    opacity: isLoading ? 0 : 1,
    visibility: isLoading ? 'hidden' : 'visible',
    pointerEvents: isLoading ? 'none' : 'auto',
    colorScheme: activeTheme === 'dark' ? 'dark' : 'light',
  };

  return (
    <div className="broadage-widget">
      <div className={ `broadage-widget__content${isLoading ? ' broadage-widget__content--loading' : ''}` }>
        { isLoading && (
          <div className="broadage-widget__loader">
            <img
              className="broadage-widget__loader-image"
              src={ BILYONER_LOADER_SRC }
              alt=""
            />
          </div>
        ) }
        <iframe
          ref={ iframeRef }
          src={ iframeSrc }
          title={ title || type || 'Broadage Widget' }
          frameBorder="0"
          scrolling={ iframeHeight ? 'no' : 'auto' }
          onLoad={ handleIframeLoad }
          style={ iframeStyle }
        />
      </div>
    </div>
  );
}

BroadageWidget.propTypes = {
  sportType: PropTypes.number,
  broadageEventId: PropTypes.number,
  broadageLeagueId: PropTypes.number,
  broadageTeamId: PropTypes.number,
  title: PropTypes.string,
  type: PropTypes.string,
  homeTeamId: PropTypes.number,
  awayTeamId: PropTypes.number,
  teamInfo: PropTypes.bool,
  roundNavigationRule: PropTypes.bool,
  dataListHeight: PropTypes.number,
  theme: PropTypes.string,
};

export default React.memo(BroadageWidget);
